import { Box, Typography } from "@mui/material";
import React, { JSX, useEffect, useMemo, useState } from "react";

/**
 * GymOwnerForm (TypeScript)
 * UI improvements applied:
 * 1) Clear top headline + tagline to reduce empty space and set context
 * 2) Thicker progress bar + “Step X of Y” label
 * 3) Stronger card separation (shadow/glow), higher contrast
 * 4) Larger, more tappable choice chips with icons + active/hover states
 * 5) Contextual “Next” CTAs (no “Keep Scrolling”), disabled until valid
 * 6) Mobile-friendly spacing, readable sizes, AA-ish contrast
 */

type ProblemKey = "retention" | "acquisition" | "coaches" | "other";
type Problem = ProblemKey | null;

type TimeSlot = {
  h: number;
  m: number;
  label: string;
};

const choice = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
function isWeekend(d: Date): boolean {
  const day = d.getDay();
  return day === 0 || day === 6;
}
function pad(n: number): string {
  return n.toString().padStart(2, "0");
}
function to12h(h: number, m: number = 0): string {
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(m)} ${suffix}`;
}
function minutesSinceMidnight(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}
function normalizeToMidnight(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Generate time slots in 30-min steps within allowed window by weekday/weekend */
function generateTimeSlotsForDate(date: Date): TimeSlot[] {
  const weekend = isWeekend(date);
  const startHour = weekend ? 7 : 16; // 7:00 vs 16:00
  const endHour = weekend ? 22 : 23; // 22:00 vs 23:00
  const slots: TimeSlot[] = [];
  for (let h = startHour; h <= endHour; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === endHour && m > 0) break; // cap at endHour:00
      slots.push({ h, m, label: to12h(h, m) });
    }
  }
  const now = new Date();
  if (sameDate(date, now)) {
    const cur = minutesSinceMidnight(now);
    return slots.filter((s) => s.h * 60 + s.m > cur);
  }
  return slots;
}

function prettyDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/* ===== Formatting helpers ===== */
function formatPhone(raw: string): string {
  const d = (raw.match(/\d/g) || []).join("");
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw || "—";
}

/* ===== Contextual placeholders for Steps 2 & 3 ===== */
const placeHolders: Record<ProblemKey, { tried: string; why: string }> = {
  acquisition: {
    tried:
      "e.g., social media ads, guest passes, referral programs, intro offers, partnerships with local businesses…",
    why: "e.g., leads were low quality, unclear offer, inconsistent follow-up, budget spread too thin, poor landing page conversion…",
  },
  retention: {
    tried:
      "e.g., weekly emails, progress reports, accountability check-ins, loyalty perks, better equipment, community events…",
    why: "e.g., members didn’t feel seen, programming mismatch, schedule friction, no clear milestones, weak habit systems…",
  },
  coaches: {
    tried:
      "e.g., semi-private training, clearer SOPs, better tools to track clients, more training for coaches, role clarity…",
    why: "e.g., inconsistent coaching standards, low buy-in, tools not adopted, KPIs unclear, training not reinforced…",
  },
  other: {
    tried:
      "Briefly describe what you tried… (marketing, operations, pricing, space, equipment, schedule, etc.)",
    why: "Why do you think it didn’t work? (misalignment, process gaps, unclear goals, lack of accountability, etc.)",
  },
};

/* ===== Component ===== */
export default function GymOwnerForm(): JSX.Element {
  // Form data
  const [problem, setProblem] = useState<Problem>(null);
  const [tried, setTried] = useState<string>("");
  const [whyFail, setWhyFail] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateLocked, setDateLocked] = useState<boolean>(false);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [phone, setPhone] = useState<string>("");

  // Flow control
  const [step, setStep] = useState<number>(1);
  const totalSteps = 6;
  const barWidth = `${Math.round(
    (Math.min(step, totalSteps) / totalSteps) * 100
  )}%`;

  // Prompts
  const step3Phrase = useMemo<string>(
    () => choice(["Gotcha.", "Noted.", "Got it.", "Okay."]),
    []
  );

  const step1Prompt = "1. What is your biggest obstacle?";
  const step2Prompt = useMemo<string>(() => {
    const base = "2. What have you tried to fix your";
    if (!problem || problem === "other") return `${base} problem?`;
    const mapping: Record<Exclude<ProblemKey, "other">, string> = {
      retention: " retention ",
      acquisition: " customer acquisition ",
      coaches: " coaches ",
    };
    const mid =
      problem in mapping
        ? mapping[problem as Exclude<ProblemKey, "other">]
        : " ";
    return `${base}${mid}problem?`;
  }, [problem]);
  const step3Prompt = `3. ${step3Phrase} Why do you think this solution did not work?`;
  const step4Prompt = `4. Book a call with us.`;
  const step5Prompt =
    "5. We will text you a reminder 15 minutes before the call.";
  const step6Prompt = `You're all set!`;

  // Calendar dates
  const today = normalizeToMidnight(new Date());

  // Booked days: next 2 days, plus +5, +7, +11 (day +3 remains open)
  const bookedSet = useMemo<Set<string>>(() => {
    const targets = [1, 2, 5, 7, 11].map((n) => addDays(today, n));
    return new Set(targets.map((d) => d.toDateString()));
  }, [today]);

  // Time slots depend on date
  const slots = useMemo<TimeSlot[]>(() => {
    if (!selectedDate) return [];
    return generateTimeSlotsForDate(selectedDate);
  }, [selectedDate]);

  // Phone validation (simple US 10 digits)
  const phoneDigits = (phone.match(/\d/g) || []).join("");
  const phoneValid = phoneDigits.length === 10;
  const formattedPhone = phone ? formatPhone(phone) : "—";

  // Auto-advance from step 1 when a choice is made
  useEffect(() => {
    if (step === 1 && problem) setStep(2);
  }, [problem, step]);

  function resetDateTime() {
    setSelectedDate(null);
    setSelectedTime(null);
    setDateLocked(false);
  }

  const triedPH =
    (problem && placeHolders[problem]?.tried) || placeHolders.other.tried;
  const whyPH =
    (problem && placeHolders[problem]?.why) || placeHolders.other.why;

  return (
    <div className="wrapper">
      <style>{css}</style>

      {/* Hero / headline to anchor the page and reduce empty space */}
      <header className="heroSection">
        <h1 className="heroTitle">Let’s identify your gym’s #1 challenge</h1>
        <p className="heroTagline">
          Answer a few quick questions → book a time → get a tailored plan.
        </p>
      </header>

      <div className="formCard" role="form" aria-label="Gym owner intake form">
        {/* Progress */}
        <div className="progressHeader">
          <div className="progressWrap" aria-hidden="true">
            <div className="progressBar" style={{ width: barWidth }} />
          </div>
          <div className="progressMeta" aria-live="polite">
            Step {step} of {totalSteps}
          </div>
        </div>

        {/* Step content */}
        <div className="stepWrap">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="step fadeSlideIn" key="step1">
              <div className="col prompt">
                <div className="promptText">{step1Prompt}</div>
              </div>

              <div className="col input">
                <div className="chipRow" role="group" aria-label="Select issue">
                  <button
                    className={`btn-chip ${
                      problem === "retention" ? "active" : ""
                    }`}
                    onClick={() => setProblem("retention")}
                    type="button"
                    aria-pressed={problem === "retention"}
                  >
                    <span className="chipIcon" aria-hidden>
                      👥
                    </span>
                    Retention
                  </button>
                  <button
                    className={`btn-chip ${
                      problem === "acquisition" ? "active" : ""
                    }`}
                    onClick={() => setProblem("acquisition")}
                    type="button"
                    aria-pressed={problem === "acquisition"}
                  >
                    <span className="chipIcon" aria-hidden>
                      📈
                    </span>
                    Customer acquisition
                  </button>
                  <button
                    className={`btn-chip ${
                      problem === "coaches" ? "active" : ""
                    }`}
                    onClick={() => setProblem("coaches")}
                    type="button"
                    aria-pressed={problem === "coaches"}
                  >
                    <span className="chipIcon" aria-hidden>
                      🧑‍🏫
                    </span>
                    Coaches
                  </button>
                  <button
                    className={`btn-chip ${
                      problem === "other" ? "active" : ""
                    }`}
                    onClick={() => setProblem("other")}
                    type="button"
                    aria-pressed={problem === "other"}
                  >
                    <span className="chipIcon" aria-hidden>
                      ✨
                    </span>
                    Other
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="step fadeSlideIn" key="step2">
              <div className="col prompt">
                <div className="promptText">{step2Prompt}</div>
                <div className="subtle">
                  Be specific—this helps us tailor next steps.
                </div>
              </div>

              <div className="col input">
                <textarea
                  className="textInput"
                  placeholder={triedPH}
                  rows={4}
                  value={tried}
                  onChange={(e) => setTried(e.target.value)}
                  aria-label="What have you tried?"
                />
                <div className="actions">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => tried.trim() && setStep(3)}
                    disabled={!tried.trim()}
                  >
                    Next →
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setProblem(null); // clear so auto-advance won’t re-trigger
                      resetDateTime();
                      setStep(1);
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="step fadeSlideIn" key="step3">
              <div className="col prompt">
                <div className="promptText">{step3Prompt}</div>
              </div>

              <div className="col input">
                <textarea
                  className="textInput"
                  placeholder={whyPH}
                  rows={4}
                  value={whyFail}
                  onChange={(e) => setWhyFail(e.target.value)}
                  aria-label="Why didn’t it work?"
                />
                <div className="actions">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => whyFail.trim() && setStep(4)}
                    disabled={!whyFail.trim()}
                  >
                    Next →
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setStep(2)}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 - Book a call */}
          {step === 4 && (
            <div className="step fadeSlideIn" key="step4">
              <div className="col prompt">
                <div className="promptText">{step4Prompt}</div>
                <div className="subtle">Pick a date, then a time.</div>
              </div>

              <div className="col input">
                {/* Date stage */}
                {!dateLocked && (
                  <>
                    <div
                      className="dateScroller"
                      role="listbox"
                      aria-label="Select a date"
                    >
                      {Array.from({ length: 30 }, (_, i) =>
                        addDays(today, i + 1)
                      ).map((d) => {
                        const booked = bookedSet.has(d.toDateString());
                        const selected = selectedDate
                          ? sameDate(d, selectedDate)
                          : false;
                        return (
                          <button
                            key={d.toISOString()}
                            type="button"
                            className={`datePill ${
                              selected ? "selected" : ""
                            } ${booked ? "disabled" : ""}`}
                            onClick={() => {
                              if (booked) return;
                              setSelectedDate(d);
                              setSelectedTime(null);
                              setTimeout(() => setDateLocked(true), 120);
                            }}
                            aria-disabled={booked}
                            aria-selected={selected}
                            title={formatDayLabel(d)}
                          >
                            <div className="pillTop">
                              <span>{formatDayLabel(d)}</span>
                              {booked && (
                                <span className="bookedBadge">
                                  Fully booked
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="legend">
                      <span className="dot disabledDot" /> Dates marked “Fully
                      booked” are unavailable
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => setStep(3)}
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}

                {/* Time stage (date chosen & locked) */}
                {dateLocked && selectedDate && (
                  <>
                    <div className="selectedDate">
                      <button
                        type="button"
                        className="dateBadge"
                        onClick={() => {
                          setDateLocked(false);
                          setSelectedTime(null);
                        }}
                        title="Click to change date"
                      >
                        {prettyDate(selectedDate)} — Change
                      </button>
                    </div>

                    <div
                      className="timeGrid"
                      role="listbox"
                      aria-label="Select a time"
                    >
                      {slots.length === 0 && (
                        <div className="muted small">
                          No times left today. Please change date.
                        </div>
                      )}
                      {slots.map((s, idx) => {
                        const selected =
                          !!selectedTime &&
                          s.h === selectedTime.h &&
                          s.m === selectedTime.m;
                        return (
                          <button
                            key={`${s.h}:${s.m}-${idx}`}
                            type="button"
                            className={`timeSlot ${selected ? "selected" : ""}`}
                            onClick={() => setSelectedTime(s)}
                            aria-selected={selected}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="actions">
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => selectedTime && setStep(5)}
                        disabled={!selectedTime}
                      >
                        Next →
                      </button>
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => {
                          setSelectedTime(null);
                          setDateLocked(false);
                          setStep(3);
                        }}
                      >
                        Back
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP 5 - Phone for SMS reminder */}
          {step === 5 && (
            <div className="step fadeSlideIn" key="step5">
              <div className="col prompt">
                <div className="promptText">{step5Prompt}</div>
              </div>

              <div className="col input">
                <input
                  className="textInput"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  aria-label="Phone number"
                />
                <Box className="muted small" sx={{ mt: 4 }}>
                  We’ll only use this to send the 15-minute reminder.
                </Box>

                <div className="actions">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => phoneValid && setStep(6)}
                    disabled={!phoneValid}
                    title={phoneValid ? "" : "Enter a valid 10-digit number"}
                  >
                    Confirm ✓
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setStep(4)}
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 6 - Confirmation + animated checkmark */}
          {step === 6 && (
            <div className="step fadeSlideIn center" key="step6">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  width: "100%",
                  gap: 2,
                }}
              >
                <Box className="checkWrap" sx={{ mr: 2 }}>
                  <svg className="checkmark" viewBox="0 0 52 52">
                    <circle
                      className="checkmark__circle"
                      cx="26"
                      cy="26"
                      r="25"
                      fill="none"
                    />
                    <path
                      className="checkmark__check"
                      fill="none"
                      d="M14 27l7 7 17-17"
                      strokeLinecap="round"
                    />
                  </svg>
                </Box>
                <Typography
                  variant="h2"
                  sx={{ textAlign: "left", alignSelf: "center", fontSize: 28 }}
                >
                  {step6Prompt}
                </Typography>
              </Box>

              {/* Summary */}
              <div className="summary">
                <dl className="summaryGrid" style={{ alignSelf: "flex-start" }}>
                  <dt>Problem</dt>
                  <dd>
                    {problem === "acquisition"
                      ? "Customer acquisition"
                      : problem || "—"}
                  </dd>

                  <dt>What you tried</dt>
                  <dd>{tried || "—"}</dd>

                  <dt>Why it didn’t work</dt>
                  <dd>{whyFail || "—"}</dd>

                  <dt>When</dt>
                  <dd>
                    {selectedDate && selectedTime
                      ? `${prettyDate(selectedDate)} at ${selectedTime.label}`
                      : "—"}
                  </dd>

                  <dt>SMS</dt>
                  <dd>{formattedPhone}</dd>
                </dl>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================== Refined Green Theme CSS ====================== */
const css = `
:root{
  --bg:#0a1210;
  --card:#0f1711;
  --muted:#a8bfb1;
  --text:#e7f4ea;
  --primary:#22c55e;           /* green-500 */
  --primary-weak: rgba(34,197,94,.16);
  --chip:#0f1711;
  --chip-border:#1a2a1f;
  --ok:#22c55e;
}
*{box-sizing:border-box}

.wrapper{
  min-height:100vh;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
  margin: 0 -16px;
  padding: 32px 0px 64px;
  color: var(--text);
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
}

/* Hero section */
.heroSection{
  width:min(980px, 100%);
  margin: 8px auto 18px;
  padding: 0 6px;
}
.heroTitle{
  margin:0;
  font-size: clamp(20px, 3.4vw, 34px);
  letter-spacing:.2px;
}
.heroTagline{
  margin:6px 0 0 0;
  color: var(--muted);
  font-size: clamp(13px, 1.6vw, 16px);
}

.formCard{
  width: min(980px, 100%);
  background: var(--card);
  border: 1px solid #152218;
  border-radius: 20px;
  box-shadow:
    0 24px 60px rgba(0,0,0,.45),
    0 0 0 1px rgba(34,197,94,.06) inset;
  padding: clamp(18px, 3vw, 28px);
}

.progressHeader{
  display:grid;
  grid-template-columns: 1fr auto;
  align-items:center;
  gap: 12px;
  margin-bottom: 16px;
}
.progressWrap{
  width:100%;
  height:10px;                 /* thicker */
  background: #0b140e;
  border-radius: 999px;
  overflow:hidden;
}
.progressBar{
  height: 100%;
  background: linear-gradient(90deg, var(--primary) 0%, #86efac 100%);
  width: 0%;
  transition: width .45s ease;
}
.progressMeta{
  font-size: 12px;
  color: var(--muted);
}

.stepWrap{
  display:flex;
  flex-direction:column;
  gap: 28px;
}

.step{
  display:grid;
  grid-template-columns: 1.1fr 1.3fr;
  gap: clamp(16px, 3vw, 36px);
  align-items:start;
}
.step.center{
  display:flex; flex-direction:column; align-items:center; text-align:left; width:100%;
}

.col.prompt{ min-width: 260px; }
.col.input{ }

/* Prompts */
.promptText{
  font-size: clamp(18px, 2.2vw, 26px);
  line-height:1.5;
  letter-spacing:.5px;
}
.subtle{ color: var(--muted); margin-top: 8px; }
.muted{ color: var(--muted) }
.small{ font-size: 12px }

/* Choice chips (larger, tappable, with icons) */
.chipRow{ display:flex; flex-wrap:wrap; gap:12px; }
.btn-chip{
  display:inline-flex; align-items:center; gap:10px;
  padding: 12px 16px;
  background: var(--chip);
  border: 1px solid var(--chip-border);
  border-radius: 999px;
  color: var(--text);
  cursor: pointer;
  transition: all .2s ease;
  font-size: 15px;
  min-height: 44px;             /* mobile tap target */
}
.chipIcon{ font-size: 16px; line-height: 1; }
.btn-chip:hover{ border-color: #263a2b; transform: translateY(-1px) }
.btn-chip.active{
  border-color: var(--primary);
  box-shadow: 0 0 0 6px var(--primary-weak) inset;
}

/* Inputs */
.textInput{
  width: 100%;
  padding: 12px 14px;
  background: #0b130d;
  border: 1px solid #18261c;
  border-radius: 12px;
  color: var(--text);
  outline: none;
  transition: border .2s ease, box-shadow .2s ease;
  font-size: 15px;
}
.textInput:focus{
  border-color: var(--primary);
  box-shadow: 0 0 0 6px var(--primary-weak);
}

/* Buttons */
.actions{ display:flex; gap: 10px; margin-top: 12px; flex-wrap:wrap; }
.btn{
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #1a2a1f;
  background: #0c140e;
  color: var(--text);
  cursor: pointer;
  transition: transform .18s ease, background .18s ease, border .18s ease, box-shadow .18s ease;
  min-height: 44px;
}
.btn:hover{ transform: translateY(-1px); border-color:#243a2a }
.btn.primary{
  background: var(--primary);
  border-color: #16a34a;
  color: #05240f;
  font-weight: 700;
  box-shadow: 0 8px 18px rgba(34,197,94,.18);
}
.btn.primary:disabled{ opacity:.55; cursor:not-allowed; transform:none; box-shadow:none }
.btn.ghost{ background: transparent; }

/* Date/time picker */
.dateScroller{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}
.datePill{
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #183020;
  background: #0c1510;
  color: var(--text);
  cursor: pointer;
  transition: all .2s ease;
  text-align:left;
}
.datePill:hover{ border-color:#24452f; transform: translateY(-1px) }
.datePill.selected{
  border-color: var(--primary);
  box-shadow: 0 0 0 6px var(--primary-weak) inset;
}
.datePill.disabled{ opacity: .45; cursor:not-allowed; }
.pillTop{ display:flex; align-items:center; justify-content:space-between; gap: 8px; }
.bookedBadge{
  font-size: 11px;
  color: #eafff1;
  background: #14532d;
  border: 1px solid #166534;
  padding: 2px 6px;
  border-radius: 999px;
}

.legend{ display:flex; align-items:center; gap:8px; margin-top:8px; color:var(--muted); font-size:12px }
.dot{ width:10px; height:10px; display:inline-block; border-radius:50% }
.disabledDot{ background: #2a3f30 }

.selectedDate{ margin-bottom: 10px; }
.dateBadge{
  display:inline-block;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px dashed var(--primary);
  background: #0b130d;
  color: var(--text);
  cursor: pointer;
}

.timeGrid{
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 8px;
  margin-top: 6px;
}
.timeSlot{
  padding: 10px 12px;
  border-radius: 10px;
  background: #0c1510;
  border: 1px solid #183020;
  color: var(--text);
  cursor: pointer;
  transition: all .2s ease;
  min-height: 44px;
}
.timeSlot:hover{ border-color:#24452f; transform: translateY(-1px) }
.timeSlot.selected{
  background: rgba(34,197,94,.12);
  border-color: var(--primary);
  box-shadow: 0 0 0 4px var(--primary-weak) inset;
}

/* Confirmation check */
.checkWrap{ margin-bottom: 14px }
.checkmark { width: 76px; height: 76px; display:block; margin: 0 auto 8px; }
.checkmark__circle {
  stroke: var(--ok);
  stroke-width: 3;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: stroke 0.8s ease forwards;
}
.checkmark__check {
  stroke: var(--ok);
  stroke-width: 4;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.5s 0.6s ease forwards;
}
@keyframes stroke { to { stroke-dashoffset: 0; } }

/* Summary */
.summary{ border-radius: 12px; }
.summaryGrid{
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 6px;
}
.summaryGrid dt{
  margin: 0;
  font-weight: 300;
  color: var(--text);
  opacity: .75;
  text-transform: capitalize;
  text-align: left;
}
.summaryGrid dd{
  margin: 0 0 10px 0;
  color: var(--text);
  word-break: break-word;
  text-align: left;
}

/* Step transition */
.fadeSlideIn{ animation: fadeSlideIn .3s ease both; }
@keyframes fadeSlideIn{
  from{ opacity:0; transform: translateY(6px) }
  to{ opacity:1; transform: translateY(0) }
}

/* Mobile */
@media (max-width: 820px){
  .step{ grid-template-columns: 1fr; }
  .col.prompt{ min-width: unset }
  .summaryGrid{ grid-template-columns: 1fr; }
  .summaryGrid dt{ opacity: .75; margin-top: 6px; text-align: left; }
  .summaryGrid dd{ margin-top: 2px; text-align: left; }
}
`;
